// UserFlow.jsx
import React, { useState, useRef, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Icon } from "@iconify/react";
import * as Yup from "yup";
import moment from "moment";
import { toast } from "sonner";
import { parse, isValid } from "date-fns";
import {
  Dialog,
  Autocomplete,
  TextField,
  Chip,
  ListItem,
  ListItemText,
  Box,
  Button,
  CircularProgress,
  Typography,
  IconButton,
} from "@mui/material";

import {
  useGetAssignees,
  createCollection,
  useGetSymptoms,
} from "../../api/collection";
import AbhaVerificationModal from "../../components/AbhaVerificationModal";
import VerificationBadge from "../../components/Badges/VerificationBadge";
import FormTextField from "../../components/Reusables/FormControls/FormTextFeilds";
import FormGenderSelect from "../../components/Reusables/FormControls/FormGenderSelection";
import FormAssignToField from "../../components/Reusables/FormControls/FormAssignToField";
import FormCollectionDatePicker from "../../components/Reusables/FormControls/FormCollectionDatePicker";
import FormField from "../../components/Reusables/FormControls/FormFeild";
import { AuthContext } from "../../context/AuthContextProvider";
import { format } from "date-fns";
import { stripEmojis, withoutEmojis } from "../../utils/emoji";
import {
  ANALYTICS_EVENTS,
  logAnalyticsEvent,
  PATIENT_TYPE,
  PATIENT_DETAILS_FETCH_METHOD,
  COLLECTION_SOURCE,
} from "../../analytics";

const userSchema = Yup.object().shape({
  first_name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("First Name is required"),
  phone: Yup.string().required("Mobile Number is required"),
  gender: Yup.string().required("Gender is required"),
  dateOfBirth: Yup.date()
    .transform((value, originalValue) => {
      if (originalValue instanceof Date) return originalValue;

    const parsed = parse(originalValue, "dd-MM-yyyy", new Date());
    return isValid(parsed) ? parsed : new Date("");
  })
  .typeError("Enter a valid date in DD-MM-YYYY format")
  .required("Date of Birth is required")
  .test(
    "valid-dob",
    "Please enter a valid Year.",
    function (value) {
      if (!value || !isValid(value)) return false;

      const year = value.getFullYear();
      const currentYear = new Date().getFullYear();

      return year >= 1900 && year <= currentYear && value <= new Date();
    }
  ),
});

const UserFlow = ({ isUser, selectedCentre, setIsUser, source }) => {
  const navigate = useNavigate();
  const triggerRef = useRef(null);
  const { open } = useContext(AuthContext);
  const [collectionSource, setCollectionSource] = useState(
    source || COLLECTION_SOURCE.GUEST
  );

  useEffect(() => {
    setCollectionSource(source || COLLECTION_SOURCE.GUEST);
  }, [source]);

  const [bodyVitals, setBodyVitals] = useState({
    height: { value: null, unit: "cm", feet: "", inches: "" },
    weight: { value: null, unit: "kg" },
    temperature: { value: null, unit: "fahrenheit" },
    bloodPressure: { systolic: null, diastolic: null, unit: "mmHg" },
    spo2: { value: null, unit: "%" },
    bloodSugar: { value: null, unit: "mg/dL" }, // NEW
    respiratoryRate: { value: null, unit: "bpm" }, // NEW
    symptoms: [],
    notes: null,
  });

  const { symptoms } = useGetSymptoms({ searchText: "" });
  const { assignees } = useGetAssignees({ centerId: selectedCentre?.value });

  // fires once, the moment the user first engages with the Additional
  // Details section (there is no dedicated "Add" button to hang this off)
  const initialBodyVitalsRef = useRef(JSON.stringify(bodyVitals));
  const additionalDetailsClickTracked = useRef(false);
  useEffect(() => {
    if (additionalDetailsClickTracked.current) return;
    if (JSON.stringify(bodyVitals) === initialBodyVitalsRef.current) return;

    additionalDetailsClickTracked.current = true;
    logAnalyticsEvent(ANALYTICS_EVENTS.ADD_ADDITIONAL_DETAILS_CLICKED);
  }, [bodyVitals]);

  const [errors, setErrors] = useState({});
  const [patientError, setPatientError] = useState(false);
  const [assigneeError, setAssigneeError] = useState(false);
  const [AbhaModal, setAbhaModal] = useState(false);
  const [assignee, setAssignee] = useState(null);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [isCreateDisabled, setIsCreateDisabled] = useState(true);

  // Secondary fields lock logic
  // const [secondaryRef, setSecondaryRef] = useState(
  //   isUser?.ref_no && isUser?.ref_no !== "null" ? isUser.ref_no : ""
  // );
  // const [secondaryEmail, setSecondaryEmail] = useState(
  //   isUser?.email && isUser?.email !== "null" ? isUser.email : ""
  // );
  // const [secondaryDob, setSecondaryDob] = useState(
  //   isUser?.dateOfBirth && isUser?.dateOfBirth !== "null"
  //     ? isUser.dateOfBirth
  //     : ""
  // );

  // refs to hold whether those fields existed for the current user (won't re-render)
  const initialSecondaryRef = React.useRef(
    isUser?.ref_no && isUser?.ref_no !== "null" ? isUser.ref_no : "",
  );
  const initialSecondaryEmail = React.useRef(
    isUser?.email && isUser?.email !== "null" ? isUser.email : "",
  );
  const initialSecondaryDob = React.useRef(
    isUser?.dateOfBirth && isUser?.dateOfBirth !== "null"
      ? isUser.dateOfBirth
      : "",
  );

  // update refs only when the user identity (id) changes
  useEffect(() => {
    initialSecondaryRef.current =
      isUser?.ref_no && isUser?.ref_no !== "null" ? isUser.ref_no : "";
    initialSecondaryEmail.current =
      isUser?.email && isUser?.email !== "null" ? isUser.email : "";
    initialSecondaryDob.current =
      isUser?.dateOfBirth && isUser?.dateOfBirth !== "null"
        ? isUser.dateOfBirth
        : "";
  }, [isUser?.id]);

  // --- MUI Autocomplete symptom helpers ---
  // symptomOptions: array of objects { label, value, description? }
  const [symptomOptions, setSymptomOptions] = useState([]);
  // selected objects (for Autocomplete value)
  const [selectedSymptomObjects, setSelectedSymptomObjects] = useState([]);

  // normalize symptoms from API into objects
  useEffect(() => {
    if (!symptoms) return;
    const normalized = symptoms.map((s) => ({
      label: s.label ?? s.name ?? s.value ?? `${s}`,
      value: s.value ?? s.label ?? s.name ?? `${s}`,
      description: s.description,
    }));
    setSymptomOptions(normalized);
  }, [symptoms]);

  // sync bodyVitals.symptoms (array of strings) to selectedSymptomObjects
  useEffect(() => {
    if (!Array.isArray(bodyVitals.symptoms)) {
      setSelectedSymptomObjects([]);
      return;
    }
    const selected = bodyVitals.symptoms.map((val) => {
      const found = symptomOptions.find((o) => o.value === val);
      return found ? found : { label: val, value: val };
    });
    setSelectedSymptomObjects(selected);
  }, [bodyVitals.symptoms, symptomOptions]);


  const handleCreateCollection = async () => {
    const addi = { ...bodyVitals };

    try {
      if (!isUser) {
        setPatientError(true);
      } else {
        setPatientError(false);
      }

      await userSchema.validate(isUser, { abortEarly: false });

      if (!assignee) {
        setAssigneeError(true);
      }

      if (!isUser || !assignee) return;

      setErrors({});

      if (isUser.id === 0) {
        logAnalyticsEvent(ANALYTICS_EVENTS.CREATE_NEW_PROFILE_FORM_FILLED);
      }

      // height conversion
      if (addi.height.unit === "ft") {
        const feet = addi.height.feet || null;
        const inches = addi.height.inches || null;
        addi.height.value = feet * 30.48 + inches * 2.54;
      }

      // weight conversion
      if (addi.weight.unit === "lbs") {
        addi.weight.value = addi.weight.value * 0.453592;
      }
      // blood sugar conversion (normalize to mg/dL)
      if (addi.bloodSugar.unit === "mmol/L") {
        addi.bloodSugar.value = addi.bloodSugar.value * 18;
      }

      const args = {
        patientPhoneNumber: isUser.phone,
        assignTo: assignee,
        patientId: isUser.patientId,
        userId: isUser.id,
        centerId: selectedCentre.value,
        medicalHistoryNotes: addi.notes,
        symptoms: addi.symptoms,
        bodyVitals: {
          height: { value: addi.height.value, unit: "cm" },
          weight: { value: addi.weight.value, unit: "kg" },
          temperature: { value: addi.temperature.value, unit: "fahrenheit" },
          blood_pressure: {
            systolic: addi.bloodPressure.systolic,
            diastolic: addi.bloodPressure.diastolic,
            unit: "mmHg",
          },
          spo2: { value: addi.spo2.value, unit: "%" },
          blood_sugar: { value: addi.bloodSugar.value, unit: "mg/dL" }, // NEW
          respiratory_rate: {
            value: addi.respiratoryRate.value,
            unit: addi.respiratoryRate.unit,
          }, // NEW
        },
        timestamp: new Date().getTime(),
        patientDetails: {
          firstName: isUser.first_name || isUser?.firstName,
          lastName: isUser.last_name,
          gender: isUser.gender,
          dateOfBirth: isUser.dateOfBirth
            ? format(parseDOB(isUser.dateOfBirth), "dd-MM-yyyy")
            : "",
          email: isUser.email,
          aadhaarNumber: isUser.aadhaarNumber,
          abhaNumber: isUser.abhaNumber,
          secondaryReferenceNumber: isUser.ref_no,
          abhaDetails: isUser.abhaDetails || {},
        },
      };

      setButtonLoading(true);
      const response = await createCollection(args);

      if (response.success) {
        toast.success(response.message);

        const hasVitals = [
          addi.height.value,
          addi.weight.value,
          addi.temperature.value,
          addi.bloodPressure.systolic,
          addi.bloodPressure.diastolic,
          addi.spo2.value,
          addi.bloodSugar.value,
          addi.respiratoryRate.value,
        ].some((v) => v !== null && v !== undefined && v !== "");

        logAnalyticsEvent(ANALYTICS_EVENTS.ADDITIONAL_DETAILS_ADDED, {
          vitals: hasVitals,
          symptoms: (addi.symptoms || []).length,
          medical_history: !!(addi.notes && String(addi.notes).trim()),
        });

        logAnalyticsEvent(ANALYTICS_EVENTS.CREATE_COLLECTION, {
          source: collectionSource,
        });

        navigate("/test-collections");
      } else {
        toast.error(response.message);
      }
    } catch (err) {
      const formattedErrors = {};
      if (err?.inner) {
        err.inner.forEach((e) => {
          if (e.path) formattedErrors[e.path] = e.message;
        });
      }
      setErrors(formattedErrors);
    } finally {
      setButtonLoading(false);
    }
  };

  // const parseDOB = (dob) => {
  //   if (!dob) return null;
  //   if (dob instanceof Date) return dob;
  //   if (typeof dob === "string" && dob.split("-")[0].length === 4) {
  //     return new Date(dob);
  //   }
  //   if (typeof dob === "string" && dob.includes("-")) {
  //     const [day, month, year] = dob.split("-");
  //     return new Date(`${year}-${month}-${day}`);
  //   }

  //   return null;
  // };
  const parseDOB = (dob) => {
    if (!dob) return null;

    if (dob instanceof Date) return dob;

    if (typeof dob === "string") {
      // try dd-MM-yyyy
      const parsed = parse(dob, "dd-MM-yyyy", new Date());
      if (isValid(parsed)) return parsed;

      // fallback ISO
      const isoParsed = new Date(dob);
      if (isValid(isoParsed)) return isoParsed;
    }

    return null;
  };

  const updateAbhaUser = (user) => {
    const userGender =
      user?.gender === "M"
        ? "male"
        : user?.gender === "F"
          ? "female"
          : user?.gender || "";

    const fullName = [
      user?.firstName,
      user?.middleName,
      user?.lastName,
    ]
      .filter(Boolean)
      .join(" ");

    setIsUser({
      first_name: user?.firstName || fullName.split(" ")[0] || "",
      last_name:
        user?.lastName ||
        fullName.split(" ").slice(1).join(" ") ||
        "",

      gender: userGender,

      dateOfBirth: parseDOB(user?.dateOfBirth || user?.dob),

      email: user?.userEmail || user?.email || "",

      aadhaarNumber: user?.aadhaarNumber || "",

      abhaNumber:
        user?.abhaNumber ||
        user?.ABHANumber ||
        "",

      phone:
        user?.phoneNumber ||
        user?.mobile ||
        "",

      uh_id: user?.patientId || "",

      age: user?.age || "",

      id: user?._id || "",

      isCreate: false,
      isAbhaVerified: true,
      isVerifiedUser: false,

      label: "Create New Patient",
      value: user?._id || "",

      patientId: user?.patientId || "",

      abhaDetails: {
        abhaNumber:
          user?.abhaNumber ||
          user?.ABHANumber ||
          "",

        abhaAddress:
          user?.abhaAddress ||
          user?.preferredAddress ||
          "",

        address:
          user?.address ||
          "",

        pinCode:
          user?.pinCode ||
          "",

        district:
          user?.district ||
          user?.districtName ||
          "",

        state:
          user?.state ||
          user?.stateName ||
          "",
      },
    });

    setCollectionSource(COLLECTION_SOURCE.VERIFIED);
    logAnalyticsEvent(ANALYTICS_EVENTS.VERIFY_PATIENT_THROUGH_ABHA_SUCCESS, {
      patient_type: isUser?.isCreate ? PATIENT_TYPE.NEW : PATIENT_TYPE.EXISTING,
    });
    logAnalyticsEvent(ANALYTICS_EVENTS.PATIENT_DETAILS_FETCHED, {
      method: PATIENT_DETAILS_FETCH_METHOD.THROUGH_ABHA,
      abha_verified: true,
    });
  };


  // Autocomplete change handler
  const handleSymptomsAutocompleteChange = (event, newValue) => {
    // newValue can contain strings (freeSolo) or objects
    const normalized = (newValue || [])
      .map((item) => {
        if (typeof item === "string") {
          return { label: stripEmojis(item), value: stripEmojis(item) };
        } else if (item && item.inputValue) {
          const clean = stripEmojis(item.inputValue);
          return { label: clean, value: clean };
        } else if (typeof item === "object") {
          return item;
        } else {
          return { label: `${item}`, value: `${item}` };
        }
      })
      // a symptom typed as nothing but emojis has no value left
      .filter((o) => String(o.value ?? "").trim() !== "");

    // add any new options to symptomOptions so they are selectable next time in this session
    const newOptionsToAdd = normalized.filter(
      (o) => !symptomOptions.some((opt) => opt.value === o.value),
    );
    if (newOptionsToAdd.length) {
      setSymptomOptions((prev) => [...prev, ...newOptionsToAdd]);
    }

    setSelectedSymptomObjects(normalized);

    setBodyVitals((prev) => ({
      ...prev,
      symptoms: normalized.map((o) => o.value),
    }));
  };
  useEffect(() => {
    if (!selectedCentre?.value || !assignee || !isUser) {
      setIsCreateDisabled(true);
      return;
    }

    try {
      const userToValidate = {
        ...isUser,
        dateOfBirth: parseDOB(isUser.dateOfBirth),
      };

      userSchema.validateSync(userToValidate, { abortEarly: false });

      setIsCreateDisabled(false);
    } catch (err) {
      setIsCreateDisabled(true);
    }
  }, [isUser, assignee, selectedCentre]);


  return (
    <>
      {selectedCentre?.isAbhaVerified &&
        (!isUser?.isAbhaVerified ? (
          <div
            className="alert alert-warning"
            onClick={() => {
              logAnalyticsEvent(ANALYTICS_EVENTS.VERIFY_PATIENT_THROUGH_ABHA, {
                patient_type: isUser?.isCreate
                  ? PATIENT_TYPE.NEW
                  : PATIENT_TYPE.EXISTING,
              });
              setAbhaModal(true);
            }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <span style={{ fontWeight: 600, fontSize: 16 }}>Verify ABHA</span>
              <i className="fas fa-chevron-right" aria-hidden="true"></i>
            </div>
          </div>
        ) : (
          <Box
            onClick={() => {
              logAnalyticsEvent(ANALYTICS_EVENTS.VERIFY_PATIENT_THROUGH_ABHA, {
                patient_type: isUser?.isCreate
                  ? PATIENT_TYPE.NEW
                  : PATIENT_TYPE.EXISTING,
              });
              setAbhaModal(true);
            }}
            sx={{
              width: "100%",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#F4F6FF",
              border: "1px solid #D6DBFF",
              cursor: "pointer",
              px: 3,
              py: 1,
              transition: "background-color 0.2s ease",
              "&:hover": {
                backgroundColor: "#EEF1FF",
              },
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                color: "#5751E9",
              }}
            >
              Re-verify ABHA Account
            </Typography>

            <IconButton
              size="small"
              sx={{
                color: "var(--app-text)",
              }}
            >
              <Icon icon="eva:chevron-right-outline" width={25} height={25} />
            </IconButton>
          </Box>
        ))}

      {/* Selected centre */}

      <Box
        sx={{
          mt: 3,
          p: 3,
          backgroundColor: "var(--app-surface)",
          borderRadius: "12px",
          boxShadow: "none",
        }}
      >
        <Typography
          sx={{
            fontSize: "15px",
            fontWeight: 500,
            color: "var(--app-text-muted)",
          }}
        >
          Selected Centre <span style={{ color: "red" }}>*</span>
        </Typography>

        <TextField
          fullWidth
          value={selectedCentre?.label || ""}
          placeholder="Selected Centre"
          disabled
          sx={{
            mt: 1.5,

            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "var(--app-surface-2)",
              height: "48px",

              "& fieldset": {
                borderColor: "var(--app-border)",
              },

              "&:hover fieldset": {
                borderColor: "var(--app-border)",
              },

              "&.Mui-focused fieldset": {
                borderColor: "var(--app-border)",
              },

              "&.Mui-disabled fieldset": {
                borderColor: "var(--app-border)",
              },
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: "var(--app-text-muted)",
            },
          }}
        />
      </Box>

      {/* Patient details */}
      <Box
        sx={{
          mt: 3,
          p: 4,
          borderRadius: "12px",
          backgroundColor: "var(--app-surface)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            fontSize={19}
            color="var(--app-text)"
          >
            Patient Details
          </Typography>

          <VerificationBadge user={isUser} />
        </Box>

        {/* Form Fields */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.33% - 16px)" } }}>
            <FormTextField
              label="First Name"
              name="first_name"
              labelColor="var(--app-text-muted)"
              required
              placeholder="First Name"
              value={isUser.first_name}
              disabled={isUser.id !== 0}
              onChange={(e) =>
                setIsUser((prev) => ({ ...prev, first_name: e.target.value }))
              }
              errors={errors}
            />
          </Box>

          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.33% - 16px)" } }}>
            <FormTextField
              label="Last Name"
              name="last_name"
              labelColor="var(--app-text-muted)"
              placeholder="Last Name"
              value={isUser.last_name}
              disabled={isUser.id !== 0}
              onChange={(e) =>
                setIsUser((prev) => ({ ...prev, last_name: e.target.value }))
              }
            />
          </Box>

          {isUser.uh_id && (
            <Box
              sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.33% - 16px)" } }}
            >
              <FormTextField
                label="UHID"
                required
                name="uh_id"
                labelColor="var(--app-text-muted)"
                placeholder="Enter UHID"
                value={isUser.uh_id}
                disabled={isUser.id !== 0}
                onChange={(e) =>
                  setIsUser((prev) => ({ ...prev, uh_id: e.target.value }))
                }
              />
            </Box>
          )}

          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.33% - 16px)" } }}>
            <FormGenderSelect
              required
              value={isUser.gender}
              labelColor="var(--app-text-muted)"
              disabled={isUser.id !== 0}
              errors={errors.gender}
              onChange={(value) =>
                setIsUser((prev) => ({ ...prev, gender: value }))
              }
            />
          </Box>

          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.33% - 16px)" } }}>
            <FormCollectionDatePicker
              label="Date of Birth"
              required
              labelColor="var(--app-text-muted)"
              value={parseDOB(isUser.dateOfBirth)}
              errors={errors.dateOfBirth}
              disabled={isUser.id !== 0 && !!initialSecondaryDob.current}
              onChange={(date) =>
                setIsUser((prev) => ({
                  ...prev,
                  dateOfBirth: date || null,
                }))
              }
            />
          </Box>

          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.33% - 16px)" } }}>
            <FormTextField
              label="Reference No."
              name="ref_no"
              labelColor="var(--app-text-muted)"
              placeholder="Enter Patient Reference No."
              value={isUser.ref_no}
              disabled={isUser.id !== 0 && !!initialSecondaryRef.current}
              onChange={(e) =>
                setIsUser((prev) => ({ ...prev, ref_no: e.target.value }))
              }
            />
          </Box>

          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 16px)" } }}>
            <FormTextField
              label="Contact Number"
              labelColor="var(--app-text-muted)"
              name="phone"
              placeholder="Enter Contact Number"
              value={isUser.phone}
              disabled={isUser.id !== 0}
              required
              onChange={(e) =>
                setIsUser((prev) => ({ ...prev, phone: e.target.value }))
              }
              errors={errors}
            />
          </Box>

          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 16px)" } }}>
            <FormTextField
              label="Email Address"
              labelColor="var(--app-text-muted)"
              name="email"
              placeholder="Enter Patient Email"
              value={isUser.email}
              disabled={isUser.id !== 0 && !!initialSecondaryEmail.current}
              onChange={(e) =>
                setIsUser((prev) => ({ ...prev, email: e.target.value }))
              }
            />
          </Box>
        </Box>

        {/* ABHA Details */}
        {isUser.isAbhaVerified && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              mt: 3,
            }}
          >
            {[
              ["ABHA Number", isUser?.abhaDetails?.abhaNumber],
              ["ABHA Address", isUser?.abhaDetails?.abhaAddress],
              ["Patient Address", isUser?.abhaDetails?.address],
              ["Pin Code", isUser?.abhaDetails?.pinCode],
              ["City", isUser?.abhaDetails?.district],
              ["State", isUser?.abhaDetails?.state],
            ].map(([label, value]) => (
              <Box
                key={label}
                sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 16px)" } }}
              >
                <FormTextField label={label} labelColor="var(--app-text-muted)" value={value || ""} disabled />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Additional details */}
      <Box
        sx={{
          mt: 3,
          p: 4,
          borderRadius: "12px",
          backgroundColor: "var(--app-surface)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            fontSize={19}
            color="var(--app-text)"
          >
            Additional Details
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {/* Height */}
          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 16px)" } }}>
            <FormField
              label="Height"
              type="height"
              heightUnit={bodyVitals.height.unit}
              value={bodyVitals.height.value}
              feet={bodyVitals.height.feet}
              inches={bodyVitals.height.inches}
              onChange={(e) =>
                setBodyVitals((prev) => ({
                  ...prev,
                  height: { ...prev.height, value: e.target.value },
                }))
              }
              onFeetChange={(e) =>
                setBodyVitals((prev) => ({
                  ...prev,
                  height: { ...prev.height, feet: e.target.value },
                }))
              }
              onInchesChange={(e) =>
                setBodyVitals((prev) => ({
                  ...prev,
                  height: { ...prev.height, inches: e.target.value },
                }))
              }
              onHeightUnitChange={(unit) =>
                setBodyVitals((prev) => ({
                  ...prev,
                  height: { ...prev.height, unit },
                }))
              }
            />
          </Box>

          {/* Weight */}
          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 16px)" } }}>
            <FormField
              label="Weight"
              type="unitSelect"
              placeholder={"Enter Weight"}
              value={bodyVitals.weight.value}
              unit={bodyVitals.weight.unit}
              unitOptions={["kg", "lbs"]}
              onChange={(e) =>
                setBodyVitals((prev) => ({
                  ...prev,
                  weight: { ...prev.weight, value: e.target.value },
                }))
              }
              onUnitChange={(unit) =>
                setBodyVitals((prev) => ({
                  ...prev,
                  weight: { ...prev.weight, unit },
                }))
              }
            />
          </Box>

          {/* Temperature */}
          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.33% - 16px)" } }}>
            <FormField
              label="Temperature"
              type="unit"
              placeholder={"Enter Temperature"}
              value={bodyVitals.temperature.value}
              unit="°F"
              onChange={(e) =>
                setBodyVitals((prev) => ({
                  ...prev,
                  temperature: { value: e.target.value },
                }))
              }
            />
          </Box>

          {/* Blood Pressure */}
          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.33% - 16px)" } }}>
            <FormField
              label="Blood Pressure"
              type="bp"
              systolic={bodyVitals.bloodPressure.systolic}
              diastolic={bodyVitals.bloodPressure.diastolic}
              unit="mm Hg"
              onChangeSys={(e) =>
                setBodyVitals((prev) => ({
                  ...prev,
                  bloodPressure: {
                    ...prev.bloodPressure,
                    systolic: e.target.value,
                  },
                }))
              }
              onChangeDia={(e) =>
                setBodyVitals((prev) => ({
                  ...prev,
                  bloodPressure: {
                    ...prev.bloodPressure,
                    diastolic: e.target.value,
                  },
                }))
              }
            />
          </Box>

          {/* SpO2 */}
          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.33% - 16px)" } }}>
            <FormField
              label="SpO2"
              type="unit"
              value={bodyVitals.spo2.value}
              unit="%"
              placeholder="Enter SpO2"
              onChange={(e) =>
                setBodyVitals((prev) => ({
                  ...prev,
                  spo2: { ...prev.spo2, value: e.target.value },
                }))
              }
            />
          </Box>

          {/* Blood Sugar (NEW) */}
          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.33% - 16px)" } }}>
            <FormField
              label="Blood Sugar"
              type="unitSelect"
              value={bodyVitals.bloodSugar.value}
              unit={bodyVitals.bloodSugar.unit}
              unitOptions={["mg/dL", "mmol/L"]}
              placeholder="Enter Blood Sugar"
              onChange={(e) =>
                setBodyVitals((prev) => ({
                  ...prev,
                  bloodSugar: { ...prev.bloodSugar, value: e.target.value },
                }))
              }
              onUnitChange={(unit) =>
                setBodyVitals((prev) => ({
                  ...prev,
                  bloodSugar: { ...prev.bloodSugar, unit },
                }))
              }
            />
          </Box>

          {/* Respiratory Rate (NEW) */}
          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.33% - 16px)" } }}>
            <FormField
              label="Respiratory Rate"
              type="unit"
              value={bodyVitals.respiratoryRate.value}
              unit={bodyVitals.respiratoryRate.unit}
              placeholder="Enter Respiratory Rate"
              onChange={(e) =>
                setBodyVitals((prev) => ({
                  ...prev,
                  respiratoryRate: {
                    ...prev.respiratoryRate,
                    value: e.target.value,
                  },
                }))
              }
            />
          </Box>

          {/* Symptoms */}
          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 100%" } }}>
            <div className="form-group">
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "var(--app-text-muted)",
                  mb: "6px",
                  fontFamily: "Figtree",
                }}
              >
                Symptoms
              </Typography>
              <div ref={triggerRef}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={symptomOptions}
                  value={selectedSymptomObjects}
                  onChange={handleSymptomsAutocompleteChange}
                  getOptionLabel={(option) =>
                    typeof option === "string"
                      ? option
                      : (option.label ?? option.value ?? "")
                  }
                  isOptionEqualToValue={(option, value) =>
                    (option?.value ?? option?.label) ===
                    (value?.value ?? value?.label)
                  }
                  filterSelectedOptions
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.label ?? option.value}
                        {...getTagProps({ index })}
                        key={option.value ?? option.label ?? index}
                      />
                    ))
                  }
                  renderOption={(props, option) => {
                    return (
                      <ListItem {...props} key={option.value}>
                        <ListItemText
                          primary={option.label}
                          secondary={option.description || null}
                        />
                      </ListItem>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        selectedSymptomObjects?.length === 0
                          ? "Select or type symptoms"
                          : ""
                      }
                      variant="outlined"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        sx: {
                          height: "52px",
                          borderRadius: "8px",
                          backgroundColor: "var(--app-surface)",
                          paddingRight: "10px",
                          color: "var(--app-text)",
                          fontWeight: 400,
                          "& input": {
                            color: "var(--app-text)",
                            fontWeight: 400,
                          },
                          "& input::placeholder": {
                            color: "var(--app-text-muted)",
                            fontWeight: 400,
                            opacity: 1,
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "var(--app-border)",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "var(--app-border)",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "var(--app-border)",
                          },
                        },
                      }}
                      inputProps={{
                        ...params.inputProps,
                        // freeSolo: clean what is typed so a chip can never
                        // carry an emoji
                        onChange: withoutEmojis(params.inputProps.onChange),
                        style: { fontWeight: 400 },
                      }}
                    />
                  )}
                />
              </div>
            </div>
          </Box>

          {/* Medical history notes */}
          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 100%" } }}>

            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "var(--app-text-muted)",
                mb: "6px",
                fontFamily: "Figtree",
              }}
            >
              Medical History Notes
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Enter Medical History Notes"
              value={bodyVitals.notes || ""}
              onChange={withoutEmojis((e) =>
                setBodyVitals((prev) => ({
                  ...prev,
                  notes: e.target.value,
                })),
              )}
              InputProps={{
                sx: {
                  borderRadius: "8px",

                  "& textarea": {
                    fontFamily: "Figtree",
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    color: "var(--app-text)",
                  },

                  "& textarea::placeholder": {
                    fontFamily: "Figtree",
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    color: "var(--app-text-muted)",
                    opacity: 1,
                  },

                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--app-border)",
                  },

                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--app-border)",
                  },

                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--app-border)",
                  },
                },
              }}
            />

          </Box>
        </Box>
      </Box>

      {/* Assign to */}
      {/* <Box
        sx={{
          mt: 3,
          p: { xs: 2, md: 4 },
          borderRadius: "12px",
          border: "1px solid #fff",
          backgroundColor: "var(--app-surface)",
          width: "100%",
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Box sx={{ width: { xs: "100%", md: "50%" } }}>
            <Box sx={{ mb: 1 }}>
              <Typography fontWeight={600} fontSize={14}>
                Assign To <span style={{ color: "red" }}>*</span>
              </Typography>
            </Box>

            <Stack style={{ width: "100%" }}>
              <Autocomplete
                fullWidth
                options={assignees || []}
                getOptionLabel={(o) => o?.name || ""}
                isOptionEqualToValue={(o, v) => o?._id === v?._id}
                onChange={(e, obj) => {
                  if (obj) setAssigneeError(false);
                  setAssignee(obj ? obj._id : null);
                }}
                renderOption={(props, item) => (
                  <li {...props} key={item._id}>
                    <div
                      className="d-flex align-items-center"
                      style={{ fontSize: "16px", fontWeight: "600" }}
                    >
                      <Avatar
                        src={item.profile}
                        alt=""
                        sx={{ width: 30, height: 30 }}
                      />
                      &nbsp;{item.name}
                    </div>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select Assignee" />
                )}
              />
            </Stack>

            {assigneeError && (
              <Typography
                sx={{
                  color: "#d32f2f",
                  fontSize: "13px",
                  mt: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Icon icon="material-symbols:info" width="18" height="18" />
                Please select an assignee
              </Typography>
            )}
          </Box>
        </Box>
      </Box> */}
      <FormAssignToField
        assignees={assignees}
        assignee={assignee}
        setAssignee={setAssignee}
        assigneeError={assigneeError}
        setAssigneeError={setAssigneeError}
      />

      {/* Bottom button */}
      <br />
      <br />
      <Box
        sx={{
          position: "fixed",
          bottom: "0px",
          padding: "10px 0",
          right: "0",
          left: "0",
          width: "100%",
          backgroundColor: "var(--app-surface-2)",

          zIndex: 1000,
          display: "flex",
          justifyContent: open ? "flex-start" : "flex-start",
        }}
      >
        <Button
          onClick={handleCreateCollection}
          disabled={isCreateDisabled || buttonLoading}
          sx={{
            width: "168px",
            height: "46px",
            borderRadius: "8px",
            padding: "10px 30px",
            gap: "10px",
            ml: open ? "17%" : "7%",

            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            lineHeight: "26px",

            backgroundColor: "#5751E9",
            color: "#ffffff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            whiteSpace: "nowrap",
            "&:hover": {
              backgroundColor: "#4b45d6",
            },
            "&.Mui-disabled": {
              backgroundColor: "var(--app-border)",
              color: "var(--app-text-muted)",
              boxShadow: "none",
              opacity: 1,
              cursor: "not-allowed",
            },
          }}
        >
          {buttonLoading ? (
            <>
              <CircularProgress size={16} sx={{ color: "#fff" }} />
              Creating…
            </>
          ) : (
            "Create New Request"
          )}
        </Button>
      </Box>

      {/* ABHA modal */}
      <Dialog
        open={AbhaModal}
        fullWidth
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setAbhaModal(false);
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            padding: 0
          }
        }}
      >
        <AbhaVerificationModal
          onClose={() => setAbhaModal(false)}
          updateAbhaUser={updateAbhaUser}
          existingPatient={isUser}
        />
      </Dialog>
    </>
  );
};

export default UserFlow;
